import moment from 'moment-timezone';
import { _firestore } from '../firebase';

const isDayToday = (dayObj) => {
  if (!dayObj?.date || typeof dayObj.date.isSame !== 'function') return false;
  try {
    const tz =
      typeof dayObj.date.tz === 'function' ? dayObj.date.tz() : undefined;
    const now = tz ? moment.tz(tz) : moment();
    return dayObj.date.isSame(now, 'day');
  } catch (_err) {
    return false;
  }
};

const TIME_REGEX = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;

const twelveHourToSeconds = (str) => {
  if (!str || typeof str !== 'string') return null;
  const match = TIME_REGEX.exec(str.trim());
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  return hours * 3600 + minutes * 60;
};

const isSlotFullForRule = (slot, rule) => {
  const maxOrders = Number(rule.maxOrders);
  const orders = Number(slot.orders) || 0;
  const maxVolume = Number(rule.orderVolume);
  const volume = Number(slot.volume) || 0;
  const type = String(rule.type);
  if (type === '0') {
    return Number.isFinite(maxOrders) && maxOrders > 0 && orders >= maxOrders;
  }
  if (type === '1') {
    return Number.isFinite(maxVolume) && maxVolume > 0 && volume >= maxVolume;
  }
  if (type === '2') {
    const ordersCapped =
      Number.isFinite(maxOrders) && maxOrders > 0 && orders >= maxOrders;
    const volumeCapped =
      Number.isFinite(maxVolume) && maxVolume > 0 && volume >= maxVolume;
    return ordersCapped && volumeCapped;
  }
  return false;
};

const ruleAppliesToContext = (rule, orderFrom, orderType) => {
  if (!rule) return false;
  if (
    !Array.isArray(rule.orderFromKeys) ||
    !rule.orderFromKeys.includes(String(orderFrom))
  ) {
    return false;
  }
  if (
    !Array.isArray(rule.orderTypeKeys) ||
    !rule.orderTypeKeys.includes(String(orderType))
  ) {
    return false;
  }
  return true;
};

const dayStringFor = (dayObj) => {
  if (!dayObj?.date || typeof dayObj.date.isoWeekday !== 'function') return null;
  try {
    return String(dayObj.date.isoWeekday());
  } catch (_err) {
    return null;
  }
};

const fetchEnabledRules = async (storeId) => {
  const snap = await _firestore
    .collection('OrderThrottlings')
    .where('storeId', '==', storeId)
    .where('isEnabled', '==', true)
    .get();
  if (!snap || snap.empty) return [];
  const rules = [];
  snap.forEach((doc) => rules.push({ _id: doc.id, ...doc.data() }));
  return rules;
};

const fetchSlotsForDate = async (storeId, dateString) => {
  console.log("fetchSlotsForDate is being called", dateString)
  const snap = await _firestore
    .collection('OrderThrottlingSlot')
    .where('storeId', '==', storeId)
    .where('date', '==', dateString)
    .get();
  if (!snap || snap.empty) return [];
  const slots = [];
  snap.forEach((doc) => slots.push({ _id: doc.id, ...doc.data() }));
  return slots;
};

const filterDay = async ({
  dayObj,
  scopedRules,
  storeId,
}) => {
  try {
    if (!dayObj || !Array.isArray(dayObj.times) || dayObj.times.length === 0) {
      return dayObj;
    }
    console.log("dayObj",dayObj)
    const dayString = dayStringFor(dayObj);
    if (!dayString) return dayObj;

    const dayRules = scopedRules.filter((r) => String(r.day) === dayString);
    if (dayRules.length === 0) return dayObj;

    const slots = await fetchSlotsForDate(storeId, dayObj.dateString);
    if (slots.length === 0) return dayObj;

    const filteredTimes = dayObj.times.filter((t) => {
      const secs = twelveHourToSeconds(t?.displayForUse);
      if (secs === null) return true;
      for (const rule of dayRules) {
        const ruleStart = Number(rule.startTime);
        const ruleEnd = Number(rule.endTime);
        if (!Number.isFinite(ruleStart) || !Number.isFinite(ruleEnd)) continue;
        if (ruleStart > secs || ruleEnd < secs) continue;

        const coveringSlot = slots.find(
          (s) =>
            s.orderThrottlingId === rule._id &&
            Number(s.startTime) <= secs &&
            Number(s.endTime) >= secs
        );
        if (!coveringSlot) continue;

        if (isSlotFullForRule(coveringSlot, rule)) return false;
      }

      return true;
    });

    if (filteredTimes.length === dayObj.times.length) return dayObj;

    return {
      ...dayObj,
      times: filteredTimes,
      timeGroup: { displayTime: filteredTimes.map((t) => t.display) },
      valueGroup: {
        displayTime: filteredTimes.length ? filteredTimes[0].display : '',
      },
    };
  } catch (_err) {
    return dayObj;
  }
};

export const filterThrottledTimeSlots = async (timeSlotDays, context) => {
  try {
    if (!Array.isArray(timeSlotDays) || timeSlotDays.length === 0) {
      return timeSlotDays;
    }
    const { storeId, orderFrom, orderType } = context || {};
    if (!storeId || !orderFrom || !orderType) return timeSlotDays;

    const allRules = await fetchEnabledRules(storeId);
    if (allRules.length === 0) return timeSlotDays;

    const scopedRules = allRules.filter((r) =>
      ruleAppliesToContext(r, orderFrom, orderType)
    );
    if (scopedRules.length === 0) return timeSlotDays;
    console.log("timeslots for today",timeSlotDays)
    const results = await Promise.all(
      timeSlotDays.map((dayObj) =>
        isDayToday(dayObj)
          ? filterDay({ dayObj, scopedRules, storeId })
          : Promise.resolve(dayObj)
      )
    );

    return results.filter(
      (d) => d && Array.isArray(d.times) && d.times.length > 0
    );
  } catch (_err) {
    return timeSlotDays;
  }
};

export default filterThrottledTimeSlots;
